/**
 * Onboarding API hooks — templates + checklist assignments.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type {
  OnboardingAssignment,
  OnboardingTemplate,
  Paginated,
} from '@shared/types';
import {
  onboardingAssignInput,
  onboardingTemplateCreateInput,
  onboardingTemplateUpdateInput,
} from '@shared/schemas/index.js';
import { z } from 'zod';

export type OnboardingTemplateCreateBody = z.input<typeof onboardingTemplateCreateInput>;
export type OnboardingTemplateUpdateBody = z.input<typeof onboardingTemplateUpdateInput>;
export type OnboardingAssignBody = z.input<typeof onboardingAssignInput>;

export const onboardingKeys = {
  all: ['onboarding'] as const,
  templates: (params?: { activeOnly?: boolean }) =>
    [...onboardingKeys.all, 'templates', params ?? {}] as const,
  template: (id: string) => [...onboardingKeys.all, 'template', id] as const,
  assignments: (params?: { employeeId?: string; status?: string }) =>
    [...onboardingKeys.all, 'assignments', params ?? {}] as const,
  assignment: (id: string) => [...onboardingKeys.all, 'assignment', id] as const,
};

export function useOnboardingTemplates(params?: { activeOnly?: boolean }) {
  return useQuery({
    queryKey: onboardingKeys.templates(params),
    queryFn: () =>
      api.get<Paginated<OnboardingTemplate>>(
        `/api/onboarding/templates${qs({
          activeOnly: params?.activeOnly ? '1' : undefined,
          limit: 100,
        })}`,
      ),
  });
}

export function useCreateOnboardingTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OnboardingTemplateCreateBody) =>
      api.post<OnboardingTemplate>('/api/onboarding/templates', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export function useUpdateOnboardingTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: OnboardingTemplateUpdateBody }) =>
      api.put<OnboardingTemplate>(`/api/onboarding/templates/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export function useOnboardingAssignments(params?: {
  employeeId?: string;
  status?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: onboardingKeys.assignments(
      params?.employeeId || params?.status
        ? { employeeId: params.employeeId, status: params.status }
        : undefined,
    ),
    queryFn: () =>
      api.get<Paginated<OnboardingAssignment>>(
        `/api/onboarding/assignments${qs({
          employeeId: params?.employeeId,
          status: params?.status,
          limit: 100,
        })}`,
      ),
    enabled: params?.enabled ?? true,
  });
}

export function useOnboardingAssignment(id: string | null) {
  return useQuery({
    queryKey: onboardingKeys.assignment(id ?? ''),
    enabled: !!id,
    queryFn: () => api.get<OnboardingAssignment>(`/api/onboarding/assignments/${id}`),
  });
}

export function useAssignOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OnboardingAssignBody) =>
      api.post<OnboardingAssignment>('/api/onboarding/assignments', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export function useCompleteOnboardingItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      itemId,
      undo,
    }: {
      assignmentId: string;
      itemId: string;
      undo?: boolean;
    }) =>
      api.post<OnboardingAssignment>(
        `/api/onboarding/assignments/${assignmentId}/items/${itemId}/complete${undo ? '?undo=1' : ''}`,
        {},
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      void queryClient.invalidateQueries({
        queryKey: onboardingKeys.assignment(vars.assignmentId),
      });
    },
  });
}

export function useCancelOnboardingAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<OnboardingAssignment>(`/api/onboarding/assignments/${id}/cancel`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}
